import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { useUserProfile } from "../../hooks/useUserProfile";
import { summarizeTableSituation } from "../../lib/summarize-table-situation";
import { vibeTypeToText } from "../../lib/vibe-type-to-text";
import { Place } from "../../types/Place";
import AddPlaceInfoModal from "../AddPlaceInfoModal/AddPlaceInfoModal";
import CheckInBox from "../CheckInBox/CheckInBox";
import { GoogleMapsLink } from "../GoogleMapsLink/GoogleMapsLink";
import { Headings } from "../Headings";
import { LabeledValue } from "../LabeledValue/LabeledValue";
import { PlaceNotesHighlights } from "../PlaceNotesHighlights/PlaceNotesHighlights";
import PlaceStatusAlert from "../PlaceStatusAlert/PlaceStatusAlert";
import { PublicUserProfileLink } from "../PublicUserProfileLink/PublicUserProfileLink";
import "./PlaceDetails.scss";

interface PlaceDetailsProps {
  place: Place;
  onClose: () => void;
}

const PlaceDetails: React.FC<PlaceDetailsProps> = ({ place, onClose }) => {
  const { isLoggedIn, isUserAdmin } = useAppAuthState();
  const { profile: vibeAuthor } = useUserProfile(place.vibeSummary?.author);
  const [showAddInfoModal, setShowAddInfoModal] = useState(false);
  const [showNewPlaceCTA, setShowNewPlaceCTA] = useState(true);

  const handleAddInfo = useCallback(() => {
    if (isLoggedIn) {
      setShowAddInfoModal(true);
    } else {
      toast.error("Please login to add more info");
    }
  }, [isLoggedIn]);

  const vibeSummary = useMemo(() => {
    if (!place.vibeSummary?.text) {
      return null;
    }

    const summary = (
      <div className="vibe-summary">
        <div className="vibe-summary-text">{place.vibeSummary.text}</div>
        {vibeAuthor && (
          <PublicUserProfileLink profile={vibeAuthor} linkToProfile />
        )}
      </div>
    );

    return <LabeledValue label="Vibe Summary" value={summary} />;
  }, [place.vibeSummary, vibeAuthor]);

  return (
    <div className="place-details-container">
      <div className="fixed-header">
        <span className="flash-container close-button" onClick={onClose}>
          <i title="Close" className="material-icons">
            close
          </i>
        </span>

        <Headings.PhoneMain>{place.name}</Headings.PhoneMain>
      </div>
      <div className="scrolling-content">
        <PlaceStatusAlert status={place.status} />
        <LabeledValue
          className="place-address"
          label="Address"
          value={
            <div>
              {place.address}{" "}
              <GoogleMapsLink label="Open in Google Maps" place={place} />
            </div>
          }
        />
        {place.website && (
          <LabeledValue label="Website" value={place.website} />
        )}
        {place.vibes && (
          <LabeledValue
            label="Vibes"
            value={place.vibes.map(vibeTypeToText).join(", ")}
          />
        )}
        {vibeSummary}
        <LabeledValue
          label="Tables"
          value={
            <div>
              {summarizeTableSituation(place.numberOfTables)}{" "}
              {place.paymentTypes && place.paymentTypes.length > 0 && (
                <>&middot; {place.paymentTypes?.join(", ")}</>
              )}
            </div>
          }
        />
        {!!place.googlePlaceId && (
          <LabeledValue
            className="place-address"
            label="Notes"
            value={
              <div>
                <PlaceNotesHighlights placeId={place.googlePlaceId} />
              </div>
            }
          />
        )}
        {showNewPlaceCTA && place.numberOfTables === -1 && (
          <div className="new-place-cta flash-container">
            <div className="heading">Tell us about {place.name}?</div>
            <div className="action-buttons">
              <button
                className="btn btn-dark"
                onClick={() => {
                  setShowNewPlaceCTA(false);
                  setShowAddInfoModal(true);
                }}
              >
                Sure, no problem!
              </button>
              <button
                className="btn btn-dark"
                onClick={() => setShowNewPlaceCTA(false)}
              >
                No, thanks.
              </button>
            </div>
          </div>
        )}
        <CheckInBox placeId={place?.googlePlaceId} />
        <div className="place-details-footer">
          <div>
            <small className="caption-cta">Know this place?</small>
            <button
              className="btn btn-outline-primary"
              onClick={handleAddInfo}
              disabled={!isLoggedIn}
            >
              Add info
            </button>
          </div>
          {isUserAdmin && (
            <Link
              className="btn btn-outline-primary"
              to={`/admin/places/${place.googlePlaceId}`}
            >
              Edit Place
            </Link>
          )}
          <Link
            className="btn btn-primary"
            to={`/place/${place.googlePlaceId}`}
          >
            View Place Page &rarr;
          </Link>
        </div>
      </div>

      <AddPlaceInfoModal
        isOpen={showAddInfoModal}
        onClose={() => setShowAddInfoModal(false)}
        googlePlaceId={place.googlePlaceId}
      />
    </div>
  );
};

export default PlaceDetails;
